import Link from "next/link";
import Image from "next/image";
import dayjs from "dayjs";
import { mwImage, stripImageSizeSuffix } from "../../../lib/common";

export type BlogPostHeroCategory = {
  name: string;
  slug?: string;
  href: string;
};

type Props = {
  post: any;
  /** Defaults to `/blog/` */
  backHref?: string;
  /** Defaults to `BACK TO BLOG` */
  backLabel?: string;
  /** Path prefix for share URLs, e.g. `/blog/` or `/product-updates/` */
  sharePathPrefix?: string;
  /** When set, replaces `post.categories` in the meta row */
  categories?: BlogPostHeroCategory[];
  /** Preformatted or parseable date; falls back to `post.modified` / `post.date` */
  updatedAt?: string;
};

function getInitials(name?: string) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function formatUpdated(updatedAt?: string, post?: any) {
  if (updatedAt) {
    const parsed = dayjs(updatedAt);
    if (parsed.isValid()) return parsed.format("D MMM YYYY");
    return updatedAt;
  }
  const fallback = post?.modified || post?.date;
  return fallback ? dayjs(fallback).format("D MMM YYYY") : null;
}

export default function BlogPostHero({
  post,
  backHref = "/blog/",
  backLabel = "BACK TO BLOG",
  sharePathPrefix = "/blog/",
  categories,
  updatedAt,
}: Props) {
  const featuredImage = mwImage(post?.featuredImage?.node?.sourceUrl);
  const prefix = sharePathPrefix.endsWith("/")
    ? sharePathPrefix
    : `${sharePathPrefix}/`;
  const slugShare = `https://middleware.io${prefix}${post?.slug}/`;
  const twitterShare =
    "https://x.com/share?url=" +
    encodeURIComponent(slugShare) +
    "&text=" +
    encodeURIComponent(post?.title || "") +
    "&via=Middleware_Labs";
  const linkedInShare =
    "https://www.linkedin.com/shareArticle?mini=true&url=" +
    encodeURIComponent(slugShare);
  const chatgptShare =
    "https://chatgpt.com/?q=" +
    encodeURIComponent(`Summarize and explain this article: ${slugShare}`);
  const claudeShare =
    "https://claude.ai/new?q=" +
    encodeURIComponent(`Summarize and explain this article: ${slugShare}`);

  const authorName = post?.author?.node?.name || "";
  const initials = getInitials(authorName);
  const authorAvatarUrl = post?.author?.node?.avatar?.url
    ? mwImage(stripImageSizeSuffix(post.author.node.avatar.url))
    : null;
  const authorSlug =
    post?.author?.node?.slug || post?.author?.node?.username || "";
  const updatedLabel = formatUpdated(updatedAt, post);

  const categoryItems: BlogPostHeroCategory[] =
    categories ??
    (post?.categories?.edges ?? []).map(
      ({ node }: { node: { name: string; slug: string } }) => ({
        name: node.name,
        slug: node.slug,
        href: `/blog/category/${node.slug}/`,
      })
    );

  return (
    <header className="blog-post-hero">
      <Link href={backHref} className="blog-back-link" prefetch={false}>
        <Image
          src="/images/blog/back-blog.svg"
          alt={backLabel}
          width={20}
          height={20}
          unoptimized
        />
        <span>{backLabel}</span>
      </Link>

      <div className="blog-post-meta">
        {categoryItems.map((cat) => (
          <Link key={cat.href + cat.name} href={cat.href} prefetch={false}>
            <span className={`category ${cat.slug || ""}`}>{cat.name}</span>
          </Link>
        ))}
        {post?.seo?.readingTime ? (
          <span className="category read-time">
            {post.seo.readingTime} mins read
          </span>
        ) : null}
      </div>

      <h1>{post?.title}</h1>

      {post?.subTitle?.subTitle ? (
        <p className="blog-post-subtitle">{post.subTitle.subTitle}</p>
      ) : null}

      {(authorName || updatedLabel) && (
        <div className="blog-author-bar">
          <div className="blog-author-bar-left">
            {authorName ? (
              <div className="blog-author-avatar" aria-hidden="true">
                {authorAvatarUrl ? (
                  <Image
                    src={authorAvatarUrl}
                    alt={authorName}
                    width={42}
                    height={42}
                    unoptimized
                  />
                ) : (
                  initials
                )}
              </div>
            ) : null}
            <div className="blog-author-meta">
              {authorName ? (
                authorSlug ? (
                  <Link
                    href={`/blog/authors/${authorSlug}/`}
                    className="blog-author-name"
                    prefetch={false}
                  >
                    {authorName}
                  </Link>
                ) : (
                  <span className="blog-author-name">{authorName}</span>
                )
              ) : null}
              {updatedLabel ? (
                <p className="blog-author-updated">Updated {updatedLabel}</p>
              ) : null}
            </div>
          </div>
          <div className="blog-share-icons">
            <Link
              href={claudeShare}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Ask Claude"
              className="blog-share-icon"
            >
              <Image
                src="/images/blog/v2/claude-ai.svg"
                alt="Claude AI"
                width={14}
                height={14}
                unoptimized
              />
            </Link>
            <Link
              href={chatgptShare}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Ask ChatGPT"
              className="blog-share-icon"
            >
              <Image
                src="/images/blog/v2/chatgpt.svg"
                alt="ChatGPT"
                width={16}
                height={16}
                unoptimized
              />
            </Link>
            <Link
              href={twitterShare}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on X"
              className="blog-share-icon"
            >
              <Image
                src="/images/blog/v2/share-x.svg"
                alt="Share on X"
                width={12}
                height={12}
                unoptimized
              />
            </Link>
            <Link
              href={linkedInShare}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              className="blog-share-icon"
            >
              <Image
                src="/images/blog/v2/share-linkedin.svg"
                alt="Share on LinkedIn"
                width={13}
                height={13}
                unoptimized
              />
            </Link>
          </div>
        </div>
      )}

      {featuredImage ? (
        <div className="blog-post-hero-image">
          <Image
            src={featuredImage}
            alt={post?.title || ""}
            width={860}
            height={460}
            priority
            unoptimized
            sizes="(max-width: 768px) 100vw, 860px"
          />
        </div>
      ) : null}
    </header>
  );
}
